class BedfolkRail extends HTMLElement {
  connectedCallback() {
    this.rail = this.querySelector('[data-bf-rail]');
    if (!this.rail) return;
    this.prev = this.querySelector('[data-bf-prev]');
    this.next = this.querySelector('[data-bf-next]');
    this.dots = this.querySelector('[data-bf-dots]');
    this.prev?.addEventListener('click', () => this.move(-1));
    this.next?.addEventListener('click', () => this.move(1));
    this.rail.addEventListener('scroll', () => this.queueUpdate(), { passive: true });
    this.resizeObserver = new ResizeObserver(() => this.buildDots());
    this.resizeObserver.observe(this.rail);
    requestAnimationFrame(() => this.buildDots());
  }

  move(direction) {
    const card = this.rail.firstElementChild;
    const gap = parseFloat(getComputedStyle(this.rail).columnGap) || 0;
    const distance = card ? card.getBoundingClientRect().width + gap : this.rail.clientWidth * 0.8;
    this.rail.scrollBy({ left: distance * direction, behavior: 'smooth' });
  }

  queueUpdate() {
    if (this.ticking) return;
    this.ticking = true;
    requestAnimationFrame(() => {
      this.update();
      this.ticking = false;
    });
  }

  buildDots() {
    if (!this.dots) {
      this.update();
      return;
    }
    const card = this.rail.firstElementChild;
    const gap = parseFloat(getComputedStyle(this.rail).columnGap) || 0;
    const step = card ? card.getBoundingClientRect().width + gap : this.rail.clientWidth;
    const pages = Math.max(1, Math.round((this.rail.scrollWidth - this.rail.clientWidth) / step) + 1);
    if (this.dots.children.length !== pages) {
      this.dots.replaceChildren(...Array.from({ length: pages }, (_, index) => {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.setAttribute('aria-label', `Go to carousel page ${index + 1}`);
        dot.addEventListener('click', () => this.rail.scrollTo({ left: step * index, behavior: 'smooth' }));
        return dot;
      }));
    }
    this.update();
  }

  update() {
    const max = Math.max(0, this.rail.scrollWidth - this.rail.clientWidth);
    const card = this.rail.firstElementChild;
    const gap = parseFloat(getComputedStyle(this.rail).columnGap) || 0;
    const step = card ? card.getBoundingClientRect().width + gap : this.rail.clientWidth;
    const active = Math.min(this.dots?.children.length - 1 || 0, Math.max(0, Math.round(this.rail.scrollLeft / step)));
    [...(this.dots?.children || [])].forEach((dot, index) => dot.toggleAttribute('aria-current', index === active));
    if (this.prev) this.prev.disabled = this.rail.scrollLeft <= 2;
    if (this.next) this.next.disabled = this.rail.scrollLeft >= max - 2;
  }

  disconnectedCallback() {
    this.resizeObserver?.disconnect();
  }
}

if (!customElements.get('bedfolk-rail')) customElements.define('bedfolk-rail', BedfolkRail);

class BedfolkFooterGallery extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    this.dialog = this.querySelector('[data-bf-footer-dialog]');
    this.dialogImage = this.querySelector('[data-bf-footer-dialog-image]');

    this.addEventListener('click', (event) => {
      const trigger = event.target.closest('[data-bf-footer-open]');
      if (trigger && this.contains(trigger)) {
        this.open(trigger);
        return;
      }
      if (event.target.closest('[data-bf-footer-close]')) this.close();
    });

    this.dialog?.addEventListener('click', (event) => {
      if (event.target === this.dialog) this.close();
    });
    this.dialog?.addEventListener('close', () => {
      this.classList.remove('is-paused');
      this.lastTrigger?.focus({ preventScroll: true });
    });
  }

  open(trigger) {
    if (!this.dialog || !this.dialogImage || !trigger.dataset.bfFooterSrc) return;
    this.lastTrigger = trigger;
    this.dialogImage.src = trigger.dataset.bfFooterSrc;
    this.dialogImage.alt = trigger.dataset.bfFooterAlt || '';
    this.classList.add('is-paused');
    if (!this.dialog.open) this.dialog.showModal();
  }

  close() {
    if (this.dialog?.open) this.dialog.close();
  }
}

if (!customElements.get('bedfolk-footer-gallery')) {
  customElements.define('bedfolk-footer-gallery', BedfolkFooterGallery);
}

class BedfolkHeaderEffect {
  constructor() {
    this.section = document.querySelector('.header-section');
    if (!this.section) return;

    this.ticking = false;
    this.update = this.update.bind(this);
    this.requestUpdate = this.requestUpdate.bind(this);
    window.addEventListener('scroll', this.requestUpdate, { passive: true });
    this.update();
  }

  requestUpdate() {
    if (this.ticking) return;
    this.ticking = true;
    requestAnimationFrame(this.update);
  }

  update() {
    this.section.classList.toggle('bf-header-is-scrolled', window.scrollY > 2);
    this.ticking = false;
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new BedfolkHeaderEffect(), { once: true });
}
else {
  new BedfolkHeaderEffect();
}
